import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Fetch available subscription plans for a user.
 * Endpoint: GET /api/subscription/plans
 */
export const getSubscriptionPlans = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/subscription/plans`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch subscription plans';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getSubscriptionPlans error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Subscribe a landlord or BNB host to a plan.
 * Endpoint: POST /api/subscription/subscribe
 */
export const subscribeToPlan = async (payload = {}) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/subscription/subscribe`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: payload.planId,
          paymentMethod: payload.paymentMethod || 'MPESA',
          paymentReference: payload.paymentReference || '',
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Subscription failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('subscribeToPlan error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch tenant bills for a specific year/month.
 * Endpoint: GET /api/billing/my-bills?year=&month=
 */
export const getMyBills = async (year, month) => {
  const params = new URLSearchParams();
  params.set('year', String(year));
  params.set('month', String(month));

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/billing/my-bills?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch bills';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getMyBills error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Record or update monthly water usage for a rental assignment.
 * Endpoint: PUT /api/billing/assignments/{assignmentId}/water-usage
 * @param {string} assignmentId
 * @param {{ year: number, month: number, unitsUsed: number }} payload
 */
export const recordWaterUsage = async (assignmentId, payload) => {
  try {
    const assignmentIdValue = String(assignmentId || '').trim();
    if (!assignmentIdValue) {
      throw new Error('Assignment ID is required to record water usage');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/billing/assignments/${encodeURIComponent(assignmentIdValue)}/water-usage`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: Number(payload.year),
          month: Number(payload.month),
          unitsUsed: Number(payload.unitsUsed),
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to record water usage';
      const error = new Error(`${errorMessage} (HTTP ${response.status})`);
      error.response = data;
      error.status = response.status;
      error.assignmentId = assignmentIdValue;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('recordWaterUsage error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a new billing plan.
 * Endpoint: POST /api/admin/plans
 * @param {{ title: string, description: string, amount: number, discount: number, billingCycle: string, active: boolean }} payload
 */
export const createPlan = async (payload) => {
  try {
    if (!payload.title || !payload.description) {
      throw new Error('Plan title and description are required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/plans`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: String(payload.title).trim(),
          description: String(payload.description).trim(),
          amount: Number(payload.amount),
          discount: Number(payload.discount || 0),
          billingCycle: String(payload.billingCycle).toUpperCase(),
          active: Boolean(payload.active),
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to create plan';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('createPlan error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch all available billing plans with pagination.
 * Endpoint: GET /api/admin/plans?activeOnly=&page=0&size=20
 * @param {{ activeOnly: boolean, page: number, size: number }} options
 */
export const getPlans = async (options = {}) => {
  try {
    const { activeOnly = false, page = 0, size = 20 } = options;
    const params = new URLSearchParams();
    params.set('activeOnly', String(activeOnly));
    params.set('page', String(page));
    params.set('size', String(size));

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/plans?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch plans';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPlans error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch a single billing plan by ID.
 * Endpoint: GET /api/admin/plans/{planId}
 */
export const getPlanById = async (planId) => {
  try {
    const id = String(planId || '').trim();
    if (!id) {
      throw new Error('Plan ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/plans/${encodeURIComponent(id)}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch plan details';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPlanById error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update an existing billing plan.
 * Endpoint: PUT /api/admin/plans/{planId}
 */
export const updatePlan = async (planId, payload) => {
  try {
    const id = String(planId || '').trim();
    if (!id) {
      throw new Error('Plan ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/plans/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: String(payload.title || '').trim(),
          description: String(payload.description || '').trim(),
          amount: Number(payload.amount),
          discount: Number(payload.discount || 0),
          billingCycle: String(payload.billingCycle || '').toUpperCase(),
          active: Boolean(payload.active),
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to update plan';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('updatePlan error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a billing plan by ID.
 * Endpoint: DELETE /api/admin/plans/{planId}
 */
export const deletePlan = async (planId) => {
  try {
    const id = String(planId || '').trim();
    if (!id) {
      throw new Error('Plan ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/admin/plans/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to delete plan';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('deletePlan error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List bills for a property (admin/owner/agent).
 * GET /api/billing/properties/{propertyId}/bills
 */
export const getPropertyBills = async (propertyId, opts = {}) => {
  try {
    const params = new URLSearchParams();
    if (opts.page != null) params.append('page', String(opts.page));
    if (opts.size != null) params.append('size', String(opts.size));
    const qs = params.toString();
    const response = await apiClient(
      `${API_BASE_URL}/api/billing/properties/${encodeURIComponent(propertyId)}/bills${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch bills'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};

/**
 * Get rent status for a property for a period (admin/owner/agent).
 * GET /api/billing/properties/{propertyId}/rent-status?year=&month=
 */
export const getPropertyRentStatus = async (propertyId, { year, month } = {}) => {
  try {
    const params = new URLSearchParams();
    if (year != null) params.append('year', String(year));
    if (month != null) params.append('month', String(month));
    const qs = params.toString();
    const response = await apiClient(
      `${API_BASE_URL}/api/billing/properties/${encodeURIComponent(propertyId)}/rent-status${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      true
    );
    const data = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(data?.message || 'Failed to fetch rent status'), { response: data, status: response.status });
    }
    return data;
  } catch (error) {
    if (!error.response) {
      throw Object.assign(new Error(error.message || 'Network error'), { isNetworkError: true });
    }
    throw error;
  }
};
